/**
 * Colour from classification, once (KAN-618 for KAN-584).
 *
 * The service classifies every row-level sync / payroll state as
 * `healthy | in_progress | problem` and serves the table from
 * `GET /api/admin/reports/:aba/sync-state-catalog` (clockout-service
 * `docs/sync-status.md` §6). This module holds that catalog for the session
 * and turns a state into one of the three existing pill tones. No component
 * may colour a state by name; every status pill asks `toneFor(state)`.
 *
 *  - healthy      → success (green)
 *  - in_progress  → info    (blue)
 *  - problem      → error   (red)
 *
 * A state the catalog does not know renders the NEUTRAL tone and logs a
 * `console.error` once per state — never green, never silently red — so a
 * state added on the server reaches the admin visibly unclassified rather
 * than wearing a guessed colour. Label text stays on every pill; colour is
 * never the only signal.
 *
 * The catalog is loaded once per session by `DataController.fetchSyncStateCatalog`
 * (cached there) and pushed here with `setSyncStateCatalog`. Until it arrives
 * every state renders neutral, silently — "not loaded yet" is not an error.
 */

export const STATUS_CLASS = Object.freeze({
    HEALTHY: 'healthy',
    IN_PROGRESS: 'in_progress',
    PROBLEM: 'problem',
});

/** The three existing pill tones the three classes map to (+ neutral for unknown / not loaded). */
export const CLASS_TONE = Object.freeze({
    [STATUS_CLASS.HEALTHY]: 'success',
    [STATUS_CLASS.IN_PROGRESS]: 'info',
    [STATUS_CLASS.PROBLEM]: 'error',
});

export const NEUTRAL_TONE = 'neutral';

/**
 * Warning code a screen raises (into its `ReportsUsersNotice`-style notice)
 * when the catalog could not be loaded and every pill is rendering neutral.
 * Admin-only vocabulary: the service never sends it.
 */
export const SYNC_STATE_CATALOG_WARNING = 'sync_state_catalog_unavailable';

let catalogByState = null;
const reportedUnknown = new Set();

/**
 * Install the served catalog. Accepts the endpoint payload (`{ states }`) or
 * the bare `states` array; anything else clears the catalog.
 * @param {{ states?: Array<{state: string, class: string}> } | Array | null} catalog
 */
export function setSyncStateCatalog(catalog) {
    const states = Array.isArray(catalog) ? catalog : catalog?.states;
    if (!Array.isArray(states)) {
        catalogByState = null;
        return;
    }
    catalogByState = new Map(states.filter((e) => e && e.state != null).map((e) => [String(e.state), e]));
    reportedUnknown.clear();
}

/** Test seam: forget the catalog and the "already reported" set. */
export function resetSyncStateCatalog() {
    catalogByState = null;
    reportedUnknown.clear();
}

/** `true` once a catalog has been installed. */
export function isSyncStateCatalogLoaded() {
    return catalogByState !== null;
}

/**
 * The catalog entry for a state, or `null` when unknown or not loaded.
 * @param {string | null | undefined} state
 * @returns {{ state: string, family: string, class: string, meaning: string } | null}
 */
export function classificationFor(state) {
    if (!catalogByState || state == null) return null;
    return catalogByState.get(String(state)) || null;
}

/**
 * The class of a state (`healthy | in_progress | problem`), or `null`.
 * @param {string | null | undefined} state
 */
export function classFor(state) {
    const entry = classificationFor(state);
    return entry && Object.values(STATUS_CLASS).includes(entry.class) ? entry.class : null;
}

/**
 * THE tone lookup every sync / payroll pill uses.
 *
 * @param {string | null | undefined} state exact server value (`COMPLETE`, `connected`, …)
 * @returns {'success' | 'info' | 'error' | 'neutral'}
 */
export function toneFor(state) {
    if (state == null) return NEUTRAL_TONE;
    const cls = classFor(state);
    if (cls) return CLASS_TONE[cls];
    if (catalogByState && !reportedUnknown.has(String(state))) {
        reportedUnknown.add(String(state));
        // eslint-disable-next-line no-console
        console.error(
            `[statusClassification] state "${state}" is not in the sync-state catalog; rendering neutral. Classify it in clockout-service Constants.REPORTS.SYNC_STATE_CLASSIFICATION.`,
        );
    }
    return NEUTRAL_TONE;
}

/**
 * The served one-line meaning of a state, or `null`.
 * @param {string | null | undefined} state
 */
export function meaningFor(state) {
    return classificationFor(state)?.meaning ?? null;
}
